/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useEffect, useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { PlusIcon, Text, UnstyledButton } from '@/shared/ui';
import { SectionCard } from '@/features/property-management/components/PropertyDetailView/SectionCard';
import { VisitDrawer } from '@/features/visits/components/VisitDrawer';
import { useAuth } from '@context/authentication';
import { VisitsService } from '@services/requests/housien-services/VisitsService';
import { formatLocalizedDate, formatLocalizedTime } from '@/shared/utils/date';
import { propertyHeadline, propertyAddressLine } from '@/shared/utils/property-display';

const STATUS_CONFIG: Record<string, { labelKey: string; className: string }> = {
  pending: { labelKey: 'profile.visits.status.pending', className: 'bg-[color-mix(in_srgb,var(--status-warning)_10%,var(--surface-primary))] text-[var(--status-warning)]' },
  confirmed: { labelKey: 'profile.visits.status.confirmed', className: 'bg-[color-mix(in_srgb,var(--seed-base)_8%,var(--surface-primary))] text-[var(--seed-base)]' },
  done: { labelKey: 'profile.visits.status.done', className: 'bg-[color-mix(in_srgb,var(--status-success)_10%,var(--surface-primary))] text-[var(--status-success)]' },
  cancelled: { labelKey: 'profile.visits.status.cancelled', className: 'bg-[color-mix(in_srgb,var(--status-error)_10%,var(--surface-primary))] text-[var(--status-error)]' },
};

/**
 * Client detail Visits tab: lists scheduled visits with a schedule CTA.
 *
 * @param props - The client and agency identifiers.
 * @returns The rendered visits tab.
 */
export function ClientVisitsTab({ clientId, agencyId }: { clientId: string; agencyId: string }) {
  const { t } = useTranslation('features/clients');
  const { user } = useAuth();
  const [visits, setVisits] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = useCallback(async (opts?: { quiet?: boolean }) => {
    if (!opts?.quiet) setIsLoading(true);
    try {
      const result = await VisitsService.getPropertyVisits(
        agencyId, 1, 100, undefined, undefined, clientId,
      );
      setVisits(result?.data ?? []);
    } catch {
      setVisits([]);
    } finally {
      if (!opts?.quiet) setIsLoading(false);
    }
  }, [agencyId, clientId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (isLoading) {
    return (
      <SectionCard data-testid="client-visits" title={t('profile.tabs.visits')}>
        <div className="flex flex-col gap-[var(--space-3)]">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-[52px] rounded-(--radius-md) bg-[var(--surface-tertiary)] animate-pulse" />
          ))}
        </div>
      </SectionCard>
    );
  }

  return (
    <>
      <SectionCard
        data-testid="client-visits"
        title={t('profile.tabs.visits')}
        titleAccessory={(
          <ScheduleVisitButton
            label={t('profile.visits.scheduleVisit', 'Agendar visita')}
            onClick={() => setDrawerOpen(true)}
          />
        )}
      >
        {visits.length === 0 ? (
          <VisitsEmpty label={t('profile.visits.empty')} />
        ) : (
          <div className="flex flex-col gap-[var(--space-3)]">
            {visits.map((visit) => (
              <VisitCard key={visit.visit_id} visit={visit} t={t} />
            ))}
          </div>
        )}
      </SectionCard>
      <VisitDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        agencyId={agencyId}
        userEmail={user?.profile?.email ?? ''}
        initialClientId={clientId}
        onSuccess={() => void load({ quiet: true })}
      />
    </>
  );
}

/**
 * Dashed call-to-action button that opens the schedule-visit drawer.
 *
 * @param props - The button label and click handler.
 * @returns The rendered schedule-visit button.
 */
function ScheduleVisitButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <UnstyledButton
      type="button"
      onClick={onClick}
      aria-label={label}
      data-testid="client-schedule-visit"
      className="flex items-center gap-(--space-1-5) rounded-(--radius-md) border border-dashed border-[var(--border-subtle)] px-(--space-3) py-(--space-1-5) text-[var(--seed-base)] cursor-pointer bg-transparent hover:bg-[var(--surface-hover)]"
    >
      <PlusIcon width={13} height={13} />
      <Text as="span" className="text-[length:var(--text-meta)] [font-weight:var(--font-semibold)]">{label}</Text>
    </UnstyledButton>
  );
}

/**
 * Empty-state placeholder shown when a client has no visits.
 *
 * @param props - The empty-state label.
 * @returns The rendered empty state.
 */
function VisitsEmpty({ label }: { label: string }) {
  return (
    <div
      data-testid="visits-empty"
      className="flex items-center justify-center h-[120px] text-[var(--text-tertiary)] text-[length:var(--text-body-sm)]"
    >
      {label}
    </div>
  );
}

/**
 * Renders a single visit row with property headline, address, date/broker meta,
 * and a status badge.
 *
 * @param props - The visit record and translation function.
 * @returns The rendered visit row.
 */
function VisitCard({
  visit,
  t,
}: {
  visit: any;
  t: TFunction;
}) {
  const dateStr = visit.visit_datetime ? formatLocalizedDate(visit.visit_datetime) : '—';
  const timeStr = visit.visit_datetime ? formatLocalizedTime(visit.visit_datetime) : '';
  const statusCfg = STATUS_CONFIG[visit.status ?? ''] ?? STATUS_CONFIG.pending;

  const prop = visit.properties?.[0];
  const title = (prop ? propertyHeadline(prop) : undefined) ?? t('profile.visits.visit');
  const address = prop ? propertyAddressLine(prop) : undefined;

  return (
    <div className="flex justify-between items-center px-[var(--space-3)] py-[var(--space-2)] rounded-(--radius-md) bg-[var(--surface-primary)] border border-[var(--border-ultra-subtle)]">
      <div className="min-w-0 flex-1">
        <Text variant="bodySm" fw="var(--font-medium)" className="truncate">
          {title}
        </Text>
        <Text variant="micro" tone="tertiary">
          {dateStr}{timeStr && ` · ${timeStr}`}
          {visit.broker_name && ` · ${visit.broker_name}`}
        </Text>
        {address && (
          <Text variant="micro" tone="secondary" className="truncate">
            {address}
          </Text>
        )}
      </div>
      <Text variant="meta" fw="var(--font-semibold)" span className={`px-[var(--space-2)] py-[var(--space-1)] rounded-(--radius-sm) whitespace-nowrap ${statusCfg.className}`}>
        {t(statusCfg.labelKey)}
      </Text>
    </div>
  );
}
